import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { SearchIcon } from 'lucide-react';
import { grey } from '@mui/material/colors';
import apiClientFormData from '../../Services/ApiClientFormData';
import { formatDatePayload } from '../../Services/FormatDatePayload';
import { useSnackbar } from '../../Context/SnackbarProvider';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { COLORS } from '../../Styles/Colors';
import { CloseButton, ModalHeader } from '../../Styles/AppStyles';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormCheckbox,
  FormField,
} from '../../Components/Form';
import { AssetEditForm, AssetFormInput } from '../Assets/AssetEditForm';
import { Asset } from '../AssetPage';

export interface KitFormInput {
  name: string;
  description: string;
  OVID: string;
  active_flag: boolean;
  assets: Asset[];
}

interface KitEditFormProps {
  onSubmit: (data: KitFormInput) => void;
  onCancel: () => void;
  defaultValues?: KitFormInput;
  isEditing?: boolean;
  isLoading?: boolean;
  /** Pre-select location when opening add kit from a filtered page. */
  initialOvid?: string;
}

export const KitEditForm: React.FC<KitEditFormProps> = ({
  onSubmit,
  onCancel,
  defaultValues,
  isEditing = false,
  isLoading = false,
  initialOvid,
}) => {
  const { showSnackbar } = useSnackbar();
  const [ovs] = useGetCustomData('/api/location/user/ovs');

  const [formData, setFormData] = useState<KitFormInput>(() => ({
    name: defaultValues?.name ?? '',
    description: defaultValues?.description ?? '',
    OVID: defaultValues?.OVID ? String(defaultValues.OVID) : initialOvid ?? '',
    active_flag: defaultValues?.active_flag ?? true,
    assets: defaultValues?.assets ?? [],
  }));

  const [selectedOvs, setSelectedOvs] = useState<string>(
    defaultValues?.OVID ? String(defaultValues.OVID) : initialOvid ?? ''
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickAddAsset, setShowQuickAddAsset] = useState(false);

  const [assetsResponse, loadingAssets] = useGetCustomData(
    selectedOvs ? `/api/assets?ovid=${selectedOvs}&limit=500` : ''
  );

  const availableAssets: Asset[] = useMemo(() => {
    const list = assetsResponse?.data ?? [];
    if (!Array.isArray(list)) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (asset: Asset) =>
        asset.name?.toLowerCase().includes(term) ||
        asset.serial_number?.toLowerCase().includes(term)
    );
  }, [assetsResponse, searchTerm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, active_flag: e.target.checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, OVID: selectedOvs || formData.OVID });
  };

  const handleOvsChange = (newOvs: string | number) => {
    const ovid = String(newOvs);
    setSelectedOvs(ovid);
    setSearchTerm('');
    setFormData((prev) => ({
      ...prev,
      OVID: ovid,
      assets: isEditing && String(defaultValues?.OVID) === ovid ? prev.assets : [],
    }));
  };

  const handleAddAsset = (asset: Asset) => {
    if (formData.assets.some((a) => a.id === asset.id)) {
      showSnackbar('Asset already added to kit', 'info', 'Information');
      return;
    }
    setFormData((prev) => ({ ...prev, assets: [...prev.assets, asset] }));
  };

  const handleRemoveAsset = (assetId: number) => {
    setFormData((prev) => ({
      ...prev,
      assets: prev.assets.filter((asset) => asset.id !== assetId),
    }));
  };

  const handleQuickAddAssetSubmit = async (assetFormData: AssetFormInput) => {
    const assetPayload = {
      name: assetFormData.name,
      serial_number: assetFormData.serial_number || null,
      ovid: selectedOvs || assetFormData.ovid || null,
      barcode: assetFormData.barcode || null,
      service_interval_months: assetFormData.service_interval_months || null,
      last_service_date: formatDatePayload(assetFormData.last_service_date ?? null),
      service_due_date: formatDatePayload(assetFormData.service_due_date || null),
      category: assetFormData.category || null,
      category_id: assetFormData.category_id || null,
      manufacturer: assetFormData.manufacturer || null,
      manufacturer_id: assetFormData.manufacturer_id || null,
      part_number: assetFormData.part_number || null,
      device_id: assetFormData.device_id || null,
      bin_location_id: assetFormData.bin_location_id || null,
      is_active: assetFormData.is_active ?? true,
      checked_out: false,
      needs_repair: false,
      needs_service: false,
      cost: assetFormData.cost ?? null,
      invoice_number: assetFormData.invoice_number ?? null,
    };

    const form = new FormData();
    form.append('data', JSON.stringify(assetPayload));
    if (assetFormData.image) {
      form.append('image', assetFormData.image);
    }

    try {
      const response = await apiClientFormData('/api/assets', { method: 'POST', body: form });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showSnackbar(errorData.error || 'Failed to add asset', 'error', 'Something went wrong!');
        return;
      }

      const newAsset: Asset = await response.json();
      setFormData((prev) => ({ ...prev, assets: [...prev.assets, newAsset] }));
      setShowQuickAddAsset(false);
      showSnackbar('Asset added and included in this kit!', 'success', 'Success!');
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to add asset', 'error', 'Something went wrong!');
    }
  };

  return (
    <>
      <Form
        embedded
        hideTitle
        onSubmit={handleSubmit}
        title={isEditing ? 'Edit Kit' : 'Add Kit'}
        subtitle="Create a kit and assign tools from the selected location."
        submitLabel={isLoading ? (isEditing ? 'Updating…' : 'Saving…') : isEditing ? 'Update Kit' : 'Save Kit'}
        onCancel={onCancel}
        isLoading={isLoading}
        disableSubmit={!formData.name.trim() || !selectedOvs}
      >
        <FormSection>
          <SectionTitle>Location</SectionTitle>
          <SectionDescription>
            {isEditing
              ? 'Location cannot be changed when editing an existing kit.'
              : 'Select the location whose tools can be added to this kit.'}
          </SectionDescription>
          <FormField label="Location" required>
            <LocationSelect
              ovs={ovs}
              selectedOvs={selectedOvs || formData.OVID}
              defaultName="Select Location"
              onChange={handleOvsChange}
              disabled={isEditing}
            />
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Kit Details</SectionTitle>
          <SectionDescription>Enter the kit name and optional description.</SectionDescription>
          <FormGrid $columns={1}>
            <FormTextField
              label="Kit Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <FormTextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>Status</SectionTitle>
          <FormCheckbox
            label="Active"
            name="active_flag"
            checked={formData.active_flag}
            onChange={handleActiveChange}
          />
        </FormSection>

        {selectedOvs && (
          <FormSection>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <SectionTitle>Tools</SectionTitle>
                <SectionDescription>Search and add assets from this location to the kit.</SectionDescription>
              </Box>
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: COLORS.teal, flexShrink: 0 }}
                onClick={() => setShowQuickAddAsset(true)}
              >
                Quick Add Tool
              </Button>
            </Box>

            <FormTextField
              label="Search by name or serial number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} />
                  </InputAdornment>
                ),
              }}
            />

            {loadingAssets ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Tools
                  </Typography>
                  <Paper sx={{ height: 250, overflow: 'auto', borderRadius: 2, boxShadow: 2 }}>
                    <List dense sx={{ p: 1 }}>
                      {availableAssets.filter((a) => !formData.assets.some((s) => s.id === a.id)).length === 0 ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No tools available to add
                          </Typography>
                        </Box>
                      ) : (
                        availableAssets
                          .filter((asset) => !formData.assets.some((a) => a.id === asset.id))
                          .map((asset) => (
                            <ListItem
                              key={asset.id}
                              sx={{ border: `1px solid ${grey[300]}`, borderRadius: 1, mb: 0.5 }}
                            >
                              <ListItemText
                                primary={asset.name}
                                secondary={
                                  <Box component="span" sx={{ color: COLORS.teal }}>
                                    {asset.serial_number ? `SN: ${asset.serial_number}` : 'No SN'}
                                  </Box>
                                }
                              />
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: COLORS.teal }}
                                onClick={() => handleAddAsset(asset)}
                              >
                                Add
                              </Button>
                            </ListItem>
                          ))
                      )}
                    </List>
                  </Paper>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Tools ({formData.assets.length})
                  </Typography>
                  <Paper sx={{ height: 250, overflow: 'auto', borderRadius: 2 }}>
                    <List dense sx={{ p: 1 }}>
                      {formData.assets.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No tools added yet
                          </Typography>
                        </Box>
                      ) : (
                        formData.assets.map((asset) => (
                          <ListItem key={asset.id} sx={{ border: `1px solid ${grey[300]}`, mb: 0.5 }}>
                            <IconButton
                              edge="start"
                              color="error"
                              size="small"
                              onClick={() => handleRemoveAsset(asset.id)}
                              sx={{ mr: 1 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                            <ListItemText
                              primary={asset.name}
                              secondary={
                                <Box component="span" sx={{ color: COLORS.teal }}>
                                  {asset.serial_number ? `SN: ${asset.serial_number}` : 'No SN'}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Paper>
                </Box>
              </Box>
            )}
          </FormSection>
        )}

        {!selectedOvs && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            Select a location to view and add tools.
          </Typography>
        )}
      </Form>

      <Modal open={showQuickAddAsset} onClose={() => setShowQuickAddAsset(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            borderRadius: 2,
            maxWidth: 820,
            width: 'calc(100% - 32px)',
            maxHeight: '90dvh',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', position: 'relative', flexShrink: 0 }}>
            <CloseButton onClick={() => setShowQuickAddAsset(false)}>×</CloseButton>
            <ModalHeader>Quick Add Tool</ModalHeader>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <AssetEditForm
              initialOvid={selectedOvs}
              hideLocation
              submitLabel="Add Tool"
              onSubmit={handleQuickAddAssetSubmit}
              onCancel={() => setShowQuickAddAsset(false)}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};
